import { ThunkAction } from "redux-thunk";
import { Action, Dispatch } from "redux";
import { setMenuData, clearMenuData } from "../../layouts/reducer";
import { RootState } from "slices";
import {
    loginRequest,
    loginSuccess,
    loginError,
    loginRequiresOtp,
    clearOtpState,
    logoutSuccess,
    setOrganizationBranding,
    setLookupData,
    clearLoginError,
    setLoginType,
    menuLoadingStart,
    menuLoadingEnd
} from "./reducer";
import { profileSuccess, setBranding } from "../profile/reducer";
import authService from '../../../services/auth.service';
import * as systemAPI from '../../../services';
import { showToast } from "../../../components/common/CustomToast";
import logger from "../../../utils/logger";
import secureStorage from "../../../utils/secureStorage";
import { transformBackendToFrontend } from "../../../layouts/LayoutMenuData";

/**
 * Fetch menu data and determine redirect path
 * Shared helper to avoid duplication in login thunks
 */
const fetchMenuAndGetRedirectPath = async (dispatch: Dispatch): Promise<string> => {
    let redirectPath = "/analytics";

    try {
        dispatch(menuLoadingStart());
        const menuResponse = await systemAPI.getMenuData();
        const modules = menuResponse.data?.modules || menuResponse.data;
        const isValidModules = Array.isArray(modules) && modules.length > 0;

        if (menuResponse.statusCode === 200 && isValidModules) {
            const transformedModules = transformBackendToFrontend(modules);
            secureStorage.setMenuData(transformedModules);
            dispatch(setMenuData(transformedModules));

            // Prefer Influencer Network as landing page if the user has access to it.
            // Falls through to the first-menu-item logic below otherwise (e.g. superadmin).
            const influencerNetwork = modules.find((m: any) =>
                m.module_code === 'LOYALTY_INFLUENCER' ||
                m.route === '/loyalty/influencers' ||
                m.link === '/loyalty/influencers'
            );

            const firstMenuItem = modules[0];
            const isAdminModule = firstMenuItem.module_id === "admin" ||
                                 firstMenuItem.module_type?.toUpperCase().startsWith("ADMIN") ||
                                 (firstMenuItem.id === "admin" && firstMenuItem.link?.startsWith("/admin")) ||
                                 firstMenuItem.route?.startsWith("/admin");

            const isAdminEntry = (m: any) =>
                m.module_id === "admin" ||
                m.module_type?.toUpperCase().startsWith("ADMIN") ||
                (m.id === "admin" && m.link?.startsWith("/admin")) ||
                m.route?.startsWith("/admin");

            if (influencerNetwork && !isAdminEntry(influencerNetwork)) {
                redirectPath = influencerNetwork.route || influencerNetwork.link || '/loyalty/influencers';
                logger.info('Landing on Influencer Network', { redirectPath });
            } else if (isAdminModule) {
                redirectPath = firstMenuItem.route || "/admin/analytics";
            } else {
                const subModules = firstMenuItem.sub_modules || firstMenuItem.subItems;
                if (subModules && Array.isArray(subModules) && subModules.length > 0) {
                    const firstSubModule = subModules[0];
                    const subModuleRoute = firstSubModule.route || firstSubModule.link;
                    redirectPath = subModuleRoute || firstMenuItem.route || firstMenuItem.link || "/dashboard";
                } else {
                    redirectPath = firstMenuItem.route || firstMenuItem.link || "/dashboard";
                }
            }
            logger.info("Menu data loaded", { redirectPath, totalModules: modules.length });
        }
    } catch (menuError: any) {
        logger.error("Error fetching menu data", { error: menuError.message });
        try {
            const cachedMenuData = secureStorage.getMenuData();
            if (cachedMenuData && Array.isArray(cachedMenuData) && cachedMenuData.length > 0) {
                const firstMenuItem = cachedMenuData[0];
                if (firstMenuItem?.link) {
                    redirectPath = firstMenuItem.link;
                }
            }
        } catch (cacheError) {
            logger.warn("Could not retrieve cached menu data", cacheError);
        }
    } finally {
        dispatch(menuLoadingEnd());
    }

    return redirectPath;
};

/**
 * Navigate to path with fallback
 */
const navigateToPath = (history: any, path: string): void => {
    setTimeout(() => {
        try {
            history(path);
        } catch (navError) {
            logger.error('Navigation failed, using window.location', navError);
            window.location.href = path;
        }
    }, 100);
};

/**
 * Handle successful login response - update stores and navigate
 */
const handleLoginSuccess = async (
    response: any,
    dispatch: Dispatch,
    history: any
): Promise<void> => {
    dispatch(loginSuccess(response));

    // Persist loginType so downstream components (PointHistoryMaster's field-user
    // branch, etc.) can distinguish corporate vs customer sessions. Only lookupUser
    // dispatched setLoginType before, which the fieldUserLogin path skips.
    // Backend field-user login response uses camelCase `userType` (web-auth.service.ts).
    const userTypeFromLogin = response.userType || response.user_type;
    if (userTypeFromLogin) {
        dispatch(setLoginType(userTypeFromLogin));
    }

    const userData = response.user;
    dispatch(profileSuccess(userData));

    const brandingData = response.branding ||
                        response.organizationBranding ||
                        (userData?.organization ? {
                            organizationId: userData.organization.id,
                            org_name: userData.organization.name,
                            logo: userData.organization.logo,
                            favicon: userData.organization.favicon
                        } : null);
    if (brandingData) {
        dispatch(setBranding(brandingData));
    }

    const redirectPath = await fetchMenuAndGetRedirectPath(dispatch);
    navigateToPath(history, redirectPath);
    showToast.success("Login successful!");
};

/**
 * Lookup User - Find organization by username/email/mobile
 * Response is unwrapped by interceptor - directly contains data
 */
export const lookupUser = (
    identifier: string
): ThunkAction<void, RootState, unknown, Action<string>> =>
async (dispatch: Dispatch) => {
    try {
        dispatch(clearLoginError());

        // Response is unwrapped - directly contains lookup data
        const response: any = await authService.lookupUser(identifier);

        // Check if response has organizationId (indicates valid lookup)
        if (response && response.organizationId) {
            dispatch(setLookupData(response));

            // Fetch organization branding if organizationId is available
            const brandingResponse: any = await authService.getOrganizationBranding(
                response.organizationId
            );

            // Branding response is also unwrapped
            if (brandingResponse && brandingResponse.organizationId) {
                dispatch(setOrganizationBranding(brandingResponse));
            }

            // Set login type based on user type
            if (response.user_type) {
                dispatch(setLoginType(response.user_type));
            }

            return response;
        }
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error.message || "User not found";
        dispatch(loginError(errorMessage));
        showToast.error(errorMessage);
        throw error;
    }
};

/**
 * Get Organization Branding
 * Response is unwrapped by interceptor - directly contains data
 */
export const getOrganizationBranding = (
    params: { subdomain?: string; organizationId?: number }
): ThunkAction<void, RootState, unknown, Action<string>> =>
async (dispatch: Dispatch) => {
    try {
        // Response is unwrapped - directly contains branding data
        const response: any = await authService.getOrganizationBranding(
            params.organizationId,
            params.subdomain
        );

        // Check if response has valid branding data
        if (response && response.organizationId) {
            dispatch(setOrganizationBranding(response));
            return response;
        }
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error.message || "Failed to load branding";
        logger.error("Branding error", { error: errorMessage });
        // Don't show error to user - branding is optional
    }
};

/**
 * Corporate Login - For Super Admin, Client Admin, Backend Staff
 * Works similar to field user login with 2FA:
 * 1. First call without OTP -> API sends OTP and returns OTP_REQUIRED
 * 2. Second call with OTP -> API validates and returns success
 */
export const corporateLogin = (
    credentials: {
        username: string;
        password: string;
        organizationId: number;
        otp?: string;
    },
    history: any
): ThunkAction<void, RootState, unknown, Action<string>> =>
async (dispatch: Dispatch) => {
    try {
        dispatch(loginRequest());

        // Call the same API endpoint with or without OTP
        // Response is unwrapped by interceptor - directly contains data
        const response = await authService.corporateLogin(credentials);

        if ('mobile' in response && !('accessToken' in response)) {
            dispatch(loginRequiresOtp({
                userId: (response as any).userId || credentials.username,
                maskedMobile: (response as any).mobile,
            }));
            showToast.info("OTP has been sent to your registered mobile number");
        } else if ('accessToken' in response && 'user' in response) {
            await handleLoginSuccess(response, dispatch, history);
        }
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error.message || "Login failed";
        dispatch(loginError(errorMessage));
        logger.error('Corporate login error', { error: errorMessage });
        showToast.error(errorMessage);
    }
};

/**
 * Field User Login - For Sales Users with Mobile + OTP
 */
export const fieldUserLogin = (
    credentials: {
        mobile: string;
        organizationId: number;
        otp?: string;
    },
    history: any
): ThunkAction<void, RootState, unknown, Action<string>> =>
async (dispatch: Dispatch) => {
    try {
        dispatch(loginRequest());
        const response = await authService.fieldUserLogin(credentials);

        if ('mobile' in response && !('accessToken' in response)) {
            dispatch(loginRequiresOtp({
                userId: (response as any).userId || credentials.mobile,
                maskedMobile: (response as any).mobile,
            }));
            showToast.info("OTP has been sent to your mobile number");
        } else if ('accessToken' in response && 'user' in response) {
            await handleLoginSuccess(response, dispatch, history);
        }
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error.message || "Login failed";
        dispatch(loginError(errorMessage));
        logger.error('Field user login error', { error: errorMessage });
        showToast.error(errorMessage);
    }
};

/**
 * Verify OTP and Complete Login
 */
export const verifyOtp = (
    userId: string,
    otpCode: string,
    history: any
): ThunkAction<void, RootState, unknown, Action<string>> =>
async (dispatch: Dispatch) => {
    try {
        dispatch(loginRequest());
        const response = await authService.verifyOTP(userId, otpCode);

        if ('accessToken' in response && 'user' in response) {
            dispatch(clearOtpState());
            await handleLoginSuccess(response, dispatch, history);

            // Show success message
            showToast.success("OTP verified successfully! Welcome.");
        }
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error.message || "Invalid OTP";
        dispatch(loginError(errorMessage));
        showToast.error(errorMessage);
    }
};

/**
 * Resend OTP - For Corporate Login
 */
export const resendOtp = (
    userId: string
): ThunkAction<void, RootState, unknown, Action<string>> =>
async (_dispatch: Dispatch) => {
    try {
        // Response is unwrapped - just need to check it succeeded (no error thrown)
        await authService.resendOTP(userId);
        showToast.success("OTP has been resent to your registered mobile number");
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error.message || "Failed to resend OTP";
        showToast.error(errorMessage);
    }
};

/**
 * Resend OTP - Generic (with userId)
 */
export const resendOtpGeneric = (
    userId: string
): ThunkAction<void, RootState, unknown, Action<string>> =>
async (_dispatch: Dispatch) => {
    try {
        // Response is unwrapped - just need to check it succeeded (no error thrown)
        await authService.resendOTP(userId);
        showToast.success("OTP has been resent");
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error.message || "Failed to resend OTP";
        showToast.error(errorMessage);
    }
};

/**
 * Refresh Access Token
 * Response is unwrapped by interceptor
 */
export const refreshToken = (): ThunkAction<void, RootState, unknown, Action<string>> =>
async (dispatch: Dispatch) => {
    try {
        const response = await authService.refreshToken();

        // Response is unwrapped - check for accessToken
        if (response && 'accessToken' in response) {
            logger.info("Token refreshed successfully");
        }
    } catch (error: any) {
        logger.error("Token refresh failed", error);
        // If refresh fails, logout user
        dispatch(logoutSuccess(true));
        secureStorage.clearAuthData();
        window.location.href = "/login";
    }
};

/**
 * Customer Login - Password-based authentication
 */
export const customerLogin = (
    credentials: {
        identifier: string;
        password: string;
        organizationId: number;
    },
    history: any
): ThunkAction<void, RootState, unknown, Action<string>> =>
async (dispatch: Dispatch) => {
    try {
        dispatch(loginRequest());
        const response = await authService.customerLogin(credentials);

        if ('accessToken' in response && 'user' in response) {
            await handleLoginSuccess(response, dispatch, history);
        } else {
            const errorMessage = (response as any).message || "Customer login failed";
            dispatch(loginError(errorMessage));
            showToast.error(errorMessage);
        }
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error.message || "Customer login failed";
        dispatch(loginError(errorMessage));
        showToast.error(errorMessage);
        logger.error('Customer login error', { error: errorMessage });
    }
};

/**
 * Customer Login with OTP - OTP-based authentication
 */
export const customerLoginWithOTP = (
    credentials: {
        identifier: string;
        otp?: string;
        organizationId: number;
    },
    history: any
): ThunkAction<void, RootState, unknown, Action<string>> =>
async (dispatch: Dispatch) => {
    try {
        dispatch(loginRequest());
        const response = await authService.customerLoginWithOTP(credentials);

        if (('mobile' in response || 'email' in response) && !('accessToken' in response)) {
            const maskedIdentifier = (response as any).mobile || (response as any).email;
            dispatch(loginRequiresOtp({
                userId: credentials.identifier,
                maskedMobile: (response as any).mobile || '',
                maskedEmail: (response as any).email || '',
            }));
            showToast.info(`OTP has been sent to ${maskedIdentifier}`);
        } else if ('accessToken' in response && 'user' in response) {
            await handleLoginSuccess(response, dispatch, history);
        } else {
            const errorMessage = (response as any).message || "Customer login failed";
            dispatch(loginError(errorMessage));
            showToast.error(errorMessage);
        }
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error.message || "Customer login failed";
        dispatch(loginError(errorMessage));
        showToast.error(errorMessage);
        logger.error('Customer OTP login error', { error: errorMessage });
    }
};

/**
 * Logout User
 */
export const logoutUser = (history?: any) => async (dispatch: Dispatch) => {
    try {
        await authService.logout();
        dispatch(logoutSuccess(true));
        dispatch(clearMenuData());
        dispatch(clearOtpState());
        logger.info("User logged out successfully");
        showToast.success("Logged out successfully");

        if (history) {
            history("/login");
        } else {
            window.location.href = "/login";
        }
    } catch (error: any) {
        logger.error("Logout error", error);
        // Clear local state even if API call fails
        dispatch(logoutSuccess(true));
        dispatch(clearMenuData());
        dispatch(clearOtpState());

        if (history) {
            history("/login");
        } else {
            window.location.href = "/login";
        }
    }
};

/**
 * Clear Login Error
 */
export const clearError = (): ThunkAction<void, RootState, unknown, Action<string>> =>
async (dispatch: Dispatch) => {
    dispatch(clearLoginError());
};

